import { API_URL, getDefaultHeaders, API_TIMEOUT, shouldLogApiCalls } from './config';

/**
 * API Client
 * 
 * A utility for making API requests with consistent configuration
 * and environment-based settings.
 */

/**
 * Makes an API request
 * @param {string} endpoint - The API endpoint (without base URL)
 * @param {Object} options - Fetch options
 * @returns {Promise} - Fetch promise
 */
export const apiRequest = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);
  
  const defaultOptions = {
    headers: getDefaultHeaders(),
    signal: controller.signal,
  };
  
  const requestOptions = {
    ...defaultOptions,
    ...options,
    credentials: 'include', // This is crucial for sending cookies
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };
  
  if (shouldLogApiCalls) {
    console.log(`API Request: ${url}`, requestOptions);
  }
  
  try {
    const response = await fetch(url, requestOptions);
    clearTimeout(timeoutId);
    
    // Handle error responses first
    if (!response.ok) {
      let errorData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json();
      } else {
        errorData = await response.text();
      }
      const error = new Error(errorData.message || 'API request failed');
      error.status = response.status;
      error.data = errorData;
      throw error;
    }
    
    // Check if this is a blob response
    if (options.responseType === 'blob') {
      return response;
    }
    
    // Parse the response JSON if available
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }
    
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    
    // Handle aborted requests
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${API_TIMEOUT}ms`);
    }
    
    throw error;
  }
};

/**
 * HTTP GET request
 */
export const get = (endpoint, options = {}) => {
  return apiRequest(endpoint, { ...options, method: 'GET' });
};

/**
 * HTTP POST request
 */
export const post = (endpoint, data, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * HTTP PUT request
 */
export const put = (endpoint, data, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * HTTP DELETE request
 */
export const del = (endpoint, options = {}) => {
  return apiRequest(endpoint, { ...options, method: 'DELETE' });
};

export default {
  get,
  post,
  put,
  delete: del,
}; 